'use client';

import {
  UseQueryOptions,
  UseMutationOptions,
  useQuery,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { agentsQueryOptions, queryKeys } from './queries';
import { agentPresetQueryKeys } from '@/entities/agent-preset/api/queries';
import { isPublicPath } from '@/shared/hooks/use-optional-auth';
import {
  createAgent,
  updateAgent,
  deleteAgent,
  restoreAgent,
  analyzeUrls,
  uploadFile,
  fetchSystemPrompts,
  fetchSystemPromptsDict,
  fetchSystemPromptByLanguage,
  createSystemPrompt,
  updateSystemPrompt,
  createOrUpdateSystemPromptsBatch,
  deleteSystemPrompt,
  translateSystemPrompt,
} from './api';
import {
  Agent,
  CreateAgentRequest,
  UpdateAgentRequest,
  AnalyzeUrlsRequest,
  AnalyzeUrlsResponse,
  UploadFileResponse,
  DeleteAgentParams,
  SystemPrompt,
  SystemPromptsDict,
  TranslateSystemPromptRequest,
  TranslateSystemPromptResponse,
} from '../types/types';

/**
 * 모든 에이전트 조회 Hook
 */
export function useAgents(options?: UseQueryOptions<Agent[], Error>) {
  return useQuery({
    ...agentsQueryOptions.fetchAgents(),
    ...options,
  });
}

/**
 * 모든 에이전트 조회 Hook (Suspense 버전)
 */
export function useSuspenseAgents() {
  return useSuspenseQuery({
    ...agentsQueryOptions.fetchAgents(),
  });
}

/**
 * 특정 에이전트 조회 Hook
 */
export function useAgent(
  agentId: string,
  options?: UseQueryOptions<Agent, Error>
) {
  return useQuery({
    ...agentsQueryOptions.fetchAgent(agentId),
    enabled: !!agentId,
    ...options,
  });
}

/**
 * 공개 에이전트 조회 Hook (인증 불필요)
 * 슬러그 페이지, 박람회/전시회 키오스크 환경에서 사용
 */
export function usePublicAgent(
  agentId: string,
  options?: UseQueryOptions<Agent, Error>
) {
  return useQuery({
    ...agentsQueryOptions.fetchPublicAgent(agentId),
    enabled: !!agentId,
    ...options,
  });
}

/**
 * 자동 모드 감지 에이전트 조회 Hook
 * - /expo/* 경로: 공개 API 사용 (인증 불필요)
 * - /{slug} 경로: 공개 API 사용 (인증 불필요)
 * - 그 외 경로: 인증된 API 사용
 *
 * CustomAvatarPage에서 사용 - 슬러그 페이지와 인증 페이지 모두 지원
 */
export function useAutoAgent(
  agentId: string,
  options?: Omit<UseQueryOptions<Agent, Error>, 'queryKey' | 'queryFn'>
) {
  const pathname = usePathname();
  const isPublicRoute = isPublicPath(pathname);

  return useQuery({
    ...(isPublicRoute
      ? agentsQueryOptions.fetchPublicAgent(agentId)
      : agentsQueryOptions.fetchAgent(agentId)),
    enabled: !!agentId,
    ...options,
  });
}

/**
 * 휴지통의 에이전트 조회 Hook
 */
export function useTrashedAgents(options?: UseQueryOptions<Agent[], Error>) {
  return useQuery({
    ...agentsQueryOptions.fetchTrashedAgents(),
    ...options,
  });
}

/**
 * 에이전트 생성 Hook
 */
export function useCreateAgent(
  options?: UseMutationOptions<Agent, Error, CreateAgentRequest>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAgentRequest) => createAgent(data),
    onSuccess: () => {
      // 에이전트 목록 캐시 무효화
      queryClient.invalidateQueries({ queryKey: queryKeys.fetchAgents() });
    },
    ...options,
  });
}

/**
 * 에이전트 수정 Hook
 */
export function useUpdateAgent(
  options?: UseMutationOptions<
    Agent,
    Error,
    { agentId: string; data: UpdateAgentRequest }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, data }: { agentId: string; data: UpdateAgentRequest }) =>
      updateAgent(agentId, data),
    onSuccess: (_, variables) => {
      // 에이전트 목록 및 해당 에이전트 상세 캐시 무효화
      queryClient.invalidateQueries({ queryKey: queryKeys.fetchAgents() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.fetchAgent(variables.agentId),
      });
      // agentPresets 캐시도 무효화 (Voice Server에 새 설정이 전송되도록)
      queryClient.invalidateQueries({
        queryKey: agentPresetQueryKeys.fetchAgentPresetById(variables.agentId),
      });
      queryClient.invalidateQueries({
        queryKey: agentPresetQueryKeys.fetchPublicAgentPresetById(variables.agentId),
      });
      // 시스템 프롬프트 캐시도 무효화
      queryClient.invalidateQueries({
        queryKey: queryKeys.systemPrompts(variables.agentId),
      });
    },
    ...options,
  });
}

/**
 * 에이전트 삭제 Hook
 */
export function useDeleteAgent(
  options?: UseMutationOptions<
    { success: boolean },
    Error,
    { agentId: string; params?: DeleteAgentParams }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, params }: { agentId: string; params?: DeleteAgentParams }) =>
      deleteAgent(agentId, params),
    onSuccess: () => {
      // 에이전트 목록 및 휴지통 캐시 무효화
      queryClient.invalidateQueries({ queryKey: queryKeys.fetchAgents() });
      queryClient.invalidateQueries({ queryKey: queryKeys.fetchTrashedAgents() });
    },
    ...options,
  });
}

/**
 * 에이전트 복원 Hook
 */
export function useRestoreAgent(
  options?: UseMutationOptions<Agent, Error, string>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agentId: string) => restoreAgent(agentId),
    onSuccess: (_, agentId) => {
      // 에이전트 목록 및 휴지통 캐시 무효화
      queryClient.invalidateQueries({ queryKey: queryKeys.fetchAgents() });
      queryClient.invalidateQueries({ queryKey: queryKeys.fetchTrashedAgents() });
      queryClient.invalidateQueries({ queryKey: queryKeys.fetchAgent(agentId) });
    },
    ...options,
  });
}

/**
 * URL 분석 Hook
 */
export function useAnalyzeUrls(
  options?: UseMutationOptions<
    AnalyzeUrlsResponse,
    Error,
    { agentId: string; data: AnalyzeUrlsRequest }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, data }: { agentId: string; data: AnalyzeUrlsRequest }) =>
      analyzeUrls(agentId, data),
    onSuccess: (_, variables) => {
      // 해당 에이전트 상세 캐시 무효화
      queryClient.invalidateQueries({
        queryKey: queryKeys.fetchAgent(variables.agentId),
      });
      // Q&A 및 Documents 캐시 무효화 (URL 분석 시 Q&A가 생성됨)
      queryClient.invalidateQueries({
        queryKey: ['qa-pairs', 'list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['documents', 'list', variables.agentId],
      });
    },
    ...options,
  });
}

/**
 * 파일 업로드 Hook
 */
export function useUploadFile(
  options?: UseMutationOptions<
    UploadFileResponse,
    Error,
    { agentId: string; formData: FormData }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, formData }: { agentId: string; formData: FormData }) =>
      uploadFile(agentId, formData),
    onSuccess: (_, variables) => {
      // 해당 에이전트 상세 캐시 무효화
      queryClient.invalidateQueries({
        queryKey: queryKeys.fetchAgent(variables.agentId),
      });
      // Q&A 및 Documents 캐시 무효화 (파일 업로드 시 Q&A가 생성됨)
      queryClient.invalidateQueries({
        queryKey: ['qa-pairs', 'list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['documents', 'list', variables.agentId],
      });
    },
    ...options,
  });
}

// ============================================================================
// System Prompts Hooks
// ============================================================================

/**
 * 시스템 프롬프트 목록 조회 Hook
 */
export function useSystemPrompts(
  agentId: string,
  options?: UseQueryOptions<SystemPrompt[], Error>
) {
  return useQuery({
    ...agentsQueryOptions.fetchSystemPrompts(agentId),
    enabled: !!agentId,
    ...options,
  });
}

/**
 * 시스템 프롬프트 딕셔너리 조회 Hook
 */
export function useSystemPromptsDict(
  agentId: string,
  options?: UseQueryOptions<SystemPromptsDict, Error>
) {
  return useQuery({
    ...agentsQueryOptions.fetchSystemPromptsDict(agentId),
    enabled: !!agentId,
    ...options,
  });
}

/**
 * 특정 언어의 시스템 프롬프트 조회 Hook
 */
export function useSystemPromptByLanguage(
  agentId: string,
  languageCode: string,
  options?: UseQueryOptions<SystemPrompt, Error>
) {
  return useQuery({
    ...agentsQueryOptions.fetchSystemPromptByLanguage(agentId, languageCode),
    enabled: !!agentId && !!languageCode,
    ...options,
  });
}

/**
 * 시스템 프롬프트 생성 Hook
 */
export function useCreateSystemPrompt(
  options?: UseMutationOptions<
    SystemPrompt,
    Error,
    { agentId: string; languageCode: string; content: string }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, languageCode, content }) =>
      createSystemPrompt(agentId, languageCode, content),
    onSuccess: (_, variables) => {
      // 시스템 프롬프트 관련 캐시 무효화
      queryClient.invalidateQueries({
        queryKey: queryKeys.systemPrompts(variables.agentId),
      });
    },
    ...options,
  });
}

/**
 * 시스템 프롬프트 수정 Hook
 */
export function useUpdateSystemPrompt(
  options?: UseMutationOptions<
    SystemPrompt,
    Error,
    { agentId: string; languageCode: string; content: string }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, languageCode, content }) =>
      updateSystemPrompt(agentId, languageCode, content),
    onSuccess: (_, variables) => {
      // 시스템 프롬프트 관련 캐시 무효화
      queryClient.invalidateQueries({
        queryKey: queryKeys.systemPrompts(variables.agentId),
      });
      // agentPresets 캐시도 무효화 (contents.greeting 등이 변경될 수 있음)
      queryClient.invalidateQueries({
        queryKey: agentPresetQueryKeys.fetchAgentPresetById(variables.agentId),
      });
      queryClient.invalidateQueries({
        queryKey: agentPresetQueryKeys.fetchPublicAgentPresetById(variables.agentId),
      });
    },
    ...options,
  });
}

/**
 * 시스템 프롬프트 일괄 생성/수정 Hook
 */
export function useCreateOrUpdateSystemPromptsBatch(
  options?: UseMutationOptions<
    SystemPrompt[],
    Error,
    { agentId: string; prompts: Record<string, string> }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, prompts }) =>
      createOrUpdateSystemPromptsBatch(agentId, prompts),
    onSuccess: (_, variables) => {
      // 시스템 프롬프트 관련 캐시 무효화
      queryClient.invalidateQueries({
        queryKey: queryKeys.systemPrompts(variables.agentId),
      });
      // agentPresets 캐시도 무효화 (contents.greeting 등이 변경될 수 있음)
      queryClient.invalidateQueries({
        queryKey: agentPresetQueryKeys.fetchAgentPresetById(variables.agentId),
      });
      queryClient.invalidateQueries({
        queryKey: agentPresetQueryKeys.fetchPublicAgentPresetById(variables.agentId),
      });
    },
    ...options,
  });
}

/**
 * 시스템 프롬프트 삭제 Hook
 */
export function useDeleteSystemPrompt(
  options?: UseMutationOptions<
    { message: string; language_code: string },
    Error,
    { agentId: string; languageCode: string; permanent?: boolean }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, languageCode, permanent = false }) =>
      deleteSystemPrompt(agentId, languageCode, permanent),
    onSuccess: (_, variables) => {
      // 시스템 프롬프트 관련 캐시 무효화
      queryClient.invalidateQueries({
        queryKey: queryKeys.systemPrompts(variables.agentId),
      });
    },
    ...options,
  });
}

/**
 * 시스템 프롬프트 자동 번역 Hook
 */
export function useTranslateSystemPrompt(
  options?: UseMutationOptions<
    TranslateSystemPromptResponse,
    Error,
    { agentId: string; data: TranslateSystemPromptRequest }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, data }) => translateSystemPrompt(agentId, data),
    onSuccess: (_, variables) => {
      // 번역 후 시스템 프롬프트 캐시는 무효화하지 않음
      // (번역 결과를 사용자가 확인 후 저장하는 플로우)
    },
    ...options,
  });
}

